import React, { useEffect, useReducer, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LOCALSTORAGE_SELECTED_STATE_CODE,
  LOCALSTORAGE_SELECTED_STATE_NAME,
  ROUTE_HOME,
  NO_STATE_SELECTED_MESSAGE,
  LOADING_DISTRICTS_MESSAGE,
  LOADING_ASSEMBLIES_MESSAGE,
  LOADING_VOTERS_MESSAGE,
  ERROR_LOAD_DISTRICTS,
  ERROR_LOAD_ASSEMBLIES,
  ERROR_LOAD_VOTERS,
  PLEASE_SELECT_DISTRICT_ASSEMBLY,
  NO_RECORDS_FOUND,
  TABLE_HEADERS_VOTER_LIST,
  STATUS_LABELS,
} from "../components/constants";
import {
  fetchDistricts,
  fetchAssemblies,
  fetchVoterList,
} from "../services/api";
import "../App.css";

/* ---------------- REDUCER ---------------- */

const initialFilterState = {
  status: "0",
  districtId: "",
  assemblyId: "",
};

function filterReducer(state, action) {
  switch (action.type) {
    case "SET_STATUS":
      return {
        status: action.payload,
        districtId: "",
        assemblyId: "",
      };

    case "SET_DISTRICT":
      return {
        ...state,
        districtId: action.payload,
        assemblyId: "",
      };

    case "SET_ASSEMBLY":
      return {
        ...state,
        assemblyId: action.payload,
      };

    default:
      return state;
  }
}

/* ---------------- COMPONENT ---------------- */

export default function VoterListPage() {
  const location = useLocation();
  const tableRef = useRef(null);

  const storedStateCode = localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_CODE);
  const storedStateName =
    localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_NAME) || "";

  const [filters, dispatch] = useReducer(
    filterReducer,
    initialFilterState
  );

  const { status, districtId, assemblyId } = filters;

  const [districts, setDistricts] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [rows, setRows] = useState([]);

  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingAssemblies, setLoadingAssemblies] = useState(false);
  const [loadingVoters, setLoadingVoters] = useState(false);
  const [error, setError] = useState(null);

  /* -------- Restore status on route change -------- */

  useEffect(() => {
    const savedStatus = sessionStorage.getItem("selectedStatus") || "0";
    dispatch({ type: "SET_STATUS", payload: savedStatus });
  }, [location.pathname]);

  /* -------- Persist status -------- */

  useEffect(() => {
    sessionStorage.setItem("selectedStatus", status);
  }, [status]);

  /* -------- Load districts -------- */

  useEffect(() => {
    if (!storedStateCode) return;

    let mounted = true;
    setLoadingDistricts(true);

    fetchDistricts(storedStateCode)
      .then((data) => mounted && setDistricts(data || []))
      .catch(() => mounted && setError(ERROR_LOAD_DISTRICTS))
      .finally(() => mounted && setLoadingDistricts(false));

    return () => {
      mounted = false;
    };
  }, [storedStateCode]);

  /* -------- Load assemblies -------- */

  useEffect(() => {
    if (!storedStateCode || !districtId) {
      setAssemblies([]);
      return;
    }

    let mounted = true;
    setLoadingAssemblies(true);

    fetchAssemblies(storedStateCode, districtId)
      .then((data) => mounted && setAssemblies(data || []))
      .catch(() => mounted && setError(ERROR_LOAD_ASSEMBLIES))
      .finally(() => mounted && setLoadingAssemblies(false));

    return () => {
      mounted = false;
    };
  }, [storedStateCode, districtId]);

  /* -------- Load voters -------- */

  useEffect(() => {
    if (!storedStateCode || !districtId || !assemblyId) {
      setRows([]);
      return;
    }

    let mounted = true;
    setLoadingVoters(true);

    fetchVoterList(storedStateCode, districtId, assemblyId, status)
      .then((data) => mounted && setRows(data || []))
      .catch(() => {
        if (mounted) {
          setError(ERROR_LOAD_VOTERS);
          setRows([]);
        }
      })
      .finally(() => mounted && setLoadingVoters(false));

    return () => {
      mounted = false;
    };
  }, [storedStateCode, districtId, assemblyId, status]);

  /* -------- Reset scroll on filter change -------- */

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [status, districtId, assemblyId]);

  const getStatusLabel = (code) =>
    STATUS_LABELS[code] || STATUS_LABELS.default;

  if (!storedStateCode) {
    return (
      <div className="main-container">
        <h2 className="section-title">{getStatusLabel(status)}</h2>
        <p>
          {NO_STATE_SELECTED_MESSAGE.split("Home")[0]}
          <Link to={ROUTE_HOME}>Home</Link> page first.
        </p>
      </div>
    );
  }

  const hideReason = getStatusLabel(status) === "In Process List";

  return (
    <div className="main-container">
      <h2 className="section-title">{getStatusLabel(status)}</h2>

      <span className="iyc-badge">
        State: <strong>{storedStateName}</strong>
      </span>

      {/* -------- Filters -------- */}

      <div className="form-row">
        <div className="form-column">
          <label>Select District</label>
          <select
            className="form-select"
            value={districtId}
            onChange={(e) =>
              dispatch({ type: "SET_DISTRICT", payload: e.target.value })
            }
            disabled={loadingDistricts}
          >
            <option value="">
              {loadingDistricts
                ? LOADING_DISTRICTS_MESSAGE
                : "Please select a district"}
            </option>
            {districts.map((d) => (
              <option key={d.code} value={d.code}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-column">
          <label>Select Assembly</label>
          <select
            className="form-select"
            value={assemblyId}
            onChange={(e) =>
              dispatch({ type: "SET_ASSEMBLY", payload: e.target.value })
            }
            disabled={loadingAssemblies || !districtId}
          >
            <option value="">
              {loadingAssemblies
                ? LOADING_ASSEMBLIES_MESSAGE
                : "Please select an assembly"}
            </option>
            {assemblies.map((a) => (
              <option key={a.code} value={a.code}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* -------- Voter List Table -------- */}

      {loadingVoters ? (
        <p>{LOADING_VOTERS_MESSAGE}</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : !districtId || !assemblyId ? (
        <p>{PLEASE_SELECT_DISTRICT_ASSEMBLY}</p>
      ) : rows.length === 0 ? (
        <p>{NO_RECORDS_FOUND}</p>
      ) : (
        <div className="scroll-top-container" ref={tableRef}>
          <div className="scroll-top-inner">
            <table className="stylish-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  {TABLE_HEADERS_VOTER_LIST.slice(
                    0,
                    hideReason ? -1 : undefined
                  ).map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
              {rows.map((r, index) => (
                <tr key={r.id}>
                  <td>{index + 1}</td>
                  <td>{r.memberId}</td>
                  <td>{r.fullName}</td>
                  <td>{r.mobile}</td>
                  <td>{r.dob}</td>
                  <td>{r.relativeName}</td>
                  <td>{r.idValue}</td>
                  {!hideReason && <td>{r.reason}</td>}
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
